import { FilterOperator, FilterState } from 'types/filter-page'

/**
 * Validates if a filter value can be committed based on its type
 * @param filterType The type of filter (e.g., 'date', 'text', etc.)
 * @param value The filter value
 * @param operator The filter operator
 * @returns true if the value is valid and can be committed
 */
export const canCommitFilterValue = (
  filterType: string,
  value: any,
  operator?: FilterOperator,
): boolean => {
  switch (filterType) {
    case 'date':
    case 'partial_date':
    case 'number':
    case 'year':
      return validateBetweenFilterValues(value, operator)

    default:
      // Most filter types don't need validation
      return true
  }
}

export const formatComparisonOperator = (operator: FilterOperator) => {
  switch (operator) {
    case 'equals':
      return 'is'
    case 'contains':
      return 'contains'
    case 'between':
      return 'is between'
    case 'lt':
      return 'is less than'
    case 'lte':
      return 'is before or equal to'
    case 'gt':
      return 'is more than'
    case 'gte':
      return 'is after or equal to'
    case 'not_equals':
      return 'is not'
    default:
      return operator
  }
}

export const formatBadgeLabel = (state: FilterState): string => {
  if (!state.value) {
    return null
  }

  // Check if this is an "all" selection
  if (Array.isArray(state.value) && state.value.length > 0) {
    const firstGroup = state.value[0]
    if (firstGroup && firstGroup._isAllSelection) {
      return 'All'
    }
  }

  if (state.definition.type === 'tags' || state.definition.type === 'group')
    return state.value.length

  return `${formatComparisonOperator(state.operator)} ${formatFilterValue(
    state.operator,
    state.value,
  )}`
}

export const formatFilterValue = (
  operator: FilterOperator,
  value: any,
): string => {
  // Handle all array values
  if (Array.isArray(value)) {
    if (operator === 'between' && value.length === 2) {
      return `${value[0]} - ${value[1]}`
    }
    // For other array values (like dropdown filters), join with comma and space
    return value.join(', ')
  }

  return value
}

export const validateBetweenFilterValues = (
  value: any,
  operator: FilterOperator,
): boolean => {
  // For 'between' operator with partial data, prevent submission
  if (operator === 'between') {
    // if both values are empty, allow clearing the filter
    if (!value || (!value[0] && !value[1])) {
      return true
    }

    // Otherwise require both dates
    if (!value[0] || !value[1]) {
      return false
    }
  }
  return true
}
